import { FormattedMessage, useIntl } from 'react-intl';
import { useFormContext, FieldError } from 'react-hook-form';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IconTrash, IconUserPlus } from '@/icons';
import IconButton from '../core/IconButton';
import Button from '../core/Button';
import { FormSection } from '../FormSection';
import { cn } from '@/helpers/classnames';
import { FormElement } from '../FormElement';
import { InputText } from '../Inputs/InputText';
import { SchemaType } from '@/hooks/useStudentFormSchema';
import { WarningBanner } from '../core/WarningBanner';
import { HelpTooltip } from '../core/HelpTooltip';
import { EMAIL_DESCRIPTION } from '@/components/Import/fieldsLegendData';

type StudentFormType = SchemaType<'create' | 'update', 'child-and-youth'>;

interface StudentGuardianEmailsProps {
  editMode: boolean;
  onClickButton?: () => void;
  canEdit?: boolean;
}

export const StudentGuardianEmails = ({
  editMode,
  onClickButton,
  canEdit,
}: StudentGuardianEmailsProps) => {
  const { formatMessage } = useIntl();
  const { watch, getValues, setValue } = useFormContext<StudentFormType>();
  const addGuardianButtonRef = useRef<HTMLButtonElement>(null);
  const shouldFocusAddGuardianRef = useRef(false);
  const shouldFocusGuardian2Ref = useRef(false);

  const guardian2Email = watch('guardian2_email');
  const hasGuardian2 = !!guardian2Email;
  const [showGuardian2, setShowGuardian2] = useState(hasGuardian2);
  const twoGuardians = showGuardian2 || hasGuardian2;

  const addGuardian = useCallback(() => {
    shouldFocusGuardian2Ref.current = true;
    setShowGuardian2(true);
  }, []);

  const removeGuardian = useCallback(
    (guardianIndex: 1 | 2) => {
      if (guardianIndex === 1) {
        setValue('guardian1_email', getValues('guardian2_email') as never, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      setValue('guardian2_email', undefined as never, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setShowGuardian2(false);
      shouldFocusAddGuardianRef.current = true;
    },
    [getValues, setValue],
  );

  const removeFirstGuardian = useCallback(() => {
    removeGuardian(1);
  }, [removeGuardian]);

  const removeSecondGuardian = useCallback(() => {
    removeGuardian(2);
  }, [removeGuardian]);

  useEffect(() => {
    if (!shouldFocusAddGuardianRef.current) return;
    if (showGuardian2 || hasGuardian2) return;
    shouldFocusAddGuardianRef.current = false;
    addGuardianButtonRef.current?.focus();
  }, [showGuardian2, hasGuardian2]);

  useEffect(() => {
    if (!shouldFocusGuardian2Ref.current || !showGuardian2) return;
    const frame = requestAnimationFrame(() => {
      document.getElementById('guardian2_email')?.focus();
      shouldFocusGuardian2Ref.current = false;
    });
    return () => cancelAnimationFrame(frame);
  }, [showGuardian2]);

  return (
    <FormSection
      title={<FormattedMessage defaultMessage="Vårdnadshavare" />}
      editMode={editMode}
      onClickButton={onClickButton}
      canSeeButton={onClickButton ? canEdit : undefined}
    >
      {editMode && (
        <WarningBanner announce={false}>
          <FormattedMessage
            id="page.students.manualEditWarning"
            defaultMessage="Manuell redigering av personer i en elevkategori kommer bli överskrivet vid nästa import om inte samma ändringar finns i importfilen."
          />
        </WarningBanner>
      )}
      <GuardianEmailField
        title={formatMessage({ defaultMessage: 'Vårdnadshavare 1' })}
        fieldName="guardian1_email"
        guardianNumber={1}
        editMode={editMode}
        className={cn(editMode && 'mb-2')}
        onRemove={editMode && twoGuardians ? removeFirstGuardian : undefined}
      />
      {twoGuardians && (
        <GuardianEmailField
          title={formatMessage({ defaultMessage: 'Vårdnadshavare 2' })}
          fieldName="guardian2_email"
          guardianNumber={2}
          editMode={editMode}
          className={cn(editMode && 'mb-2')}
          onRemove={editMode ? removeSecondGuardian : undefined}
        />
      )}
      {editMode && !twoGuardians && (
        <div className="w-full rounded-md border border-dashed border-background-400 p-4">
          <Button
            ref={addGuardianButtonRef}
            startIcon={<IconUserPlus />}
            onClick={addGuardian}
          >
            <FormattedMessage defaultMessage="Lägg till vårdnadshavare" />
          </Button>
        </div>
      )}
    </FormSection>
  );
};

interface GuardianEmailFieldProps {
  title: string;
  fieldName: 'guardian1_email' | 'guardian2_email';
  guardianNumber: 1 | 2;
  editMode: boolean;
  className?: string;
  onRemove?: () => void;
}

const GuardianEmailField = ({
  title,
  fieldName,
  guardianNumber,
  editMode,
  className,
  onRemove,
}: GuardianEmailFieldProps) => {
  const { formatMessage } = useIntl();
  const {
    control,
    formState: { errors },
  } = useFormContext<StudentFormType>();

  const emailError = errors[fieldName] as FieldError | undefined;

  return (
    <div
      className={cn(
        'flex flex-col rounded-md border border-dashed border-background-400',
        className,
      )}
    >
      <h3 className="flex h-fit w-full items-center rounded-md bg-background-100 p-2 text-base font-semibold">
        {title}
        {onRemove && (
          <IconButton
            aria-label={formatMessage(
              { defaultMessage: 'Ta bort vårdnadshavare {number}' },
              { number: guardianNumber },
            )}
            icon={<IconTrash aria-hidden="true" className="size-5" />}
            className="ml-auto"
            onClick={onRemove}
          />
        )}
      </h3>
      <div className="flex flex-col gap-2 p-4">
        <FormElement
          id={fieldName}
          label={<FormattedMessage defaultMessage="E-post" />}
          labelHint={<HelpTooltip text={EMAIL_DESCRIPTION} />}
          input={
            <InputText
              name={fieldName}
              control={control}
              editMode={editMode}
              error={emailError}
              helperTextReserveSpace
            />
          }
        />
      </div>
    </div>
  );
};
